import React, { useState, useEffect } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import GlassCard from './GlassCard';

const AnimatedNumber = ({ value, duration = 1000 }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setDisplayValue(Math.floor(easeOutQuart * value));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration]);

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  return (
    <Typography 
      variant="h3" 
      fontWeight="bold" 
      sx={{ 
        background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        fontSize: { xs: '1.8rem', sm: '2.2rem' }
      }}
    >
      {formatNumber(displayValue)}
    </Typography>
  );
};

const StatsCard = ({ 
  icon: Icon, 
  value, 
  label, 
  color = '#6366F1',
  trend,
  delay = 0 
}) => {
  const theme = useTheme();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ 
        duration: 0.6, 
        delay,
        type: "spring",
        stiffness: 100
      }}
      whileHover={{ scale: 1.02 }}
    >
      <GlassCard 
        variant="stats"
        sx={{ 
          height: '100%',
          minHeight: 140,
          position: 'relative',
          overflow: 'hidden'
        }}
        contentSx={{ 
          textAlign: 'center', 
          py: 3,
          position: 'relative',
          zIndex: 2
        }}
      >
        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${color}15, ${color}05)`,
            zIndex: 1,
          }}
        />
        
        {/* Icon */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ 
            duration: 0.8, 
            delay: delay + 0.2,
            type: "spring",
            stiffness: 200
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 56,
              height: 56,
              borderRadius: '16px',
              background: `linear-gradient(135deg, ${color}20, ${color}10)`,
              border: `2px solid ${color}30`,
              mb: 2,
              position: 'relative',
              zIndex: 2,
            }}
          >
            <Icon sx={{ fontSize: 28, color }} />
          </Box>
        </motion.div>

        {/* Value */}
        {typeof value === 'number' ? (
          <AnimatedNumber value={value} duration={1200} />
        ) : (
          <Typography 
            variant="h4" 
            fontWeight="bold" 
            sx={{ 
              color: theme.palette.text.primary,
              mb: 1,
              fontSize: { xs: '1.5rem', sm: '1.75rem' }
            }}
          >
            {value}
          </Typography>
        )}

        {/* Label */}
        <Typography 
          variant="body2" 
          sx={{ 
            color: theme.palette.text.secondary,
            fontWeight: 500,
            mt: 0.5,
            fontSize: '0.875rem'
          }}
        >
          {label}
        </Typography>

        {/* Trend indicator */}
        {trend && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: delay + 0.8 }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: 1,
                gap: 0.5
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: trend.value > 0 ? theme.palette.success.main : theme.palette.error.main,
                  fontWeight: 600,
                  fontSize: '0.75rem'
                }}
              >
                {trend.value > 0 ? '↗' : '↘'} {Math.abs(trend.value)}%
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary }}
              >
                {trend.period}
              </Typography>
            </Box>
          </motion.div>
        )}
      </GlassCard>
    </motion.div>
  );
};

export default StatsCard;
